import { DatePicker as AntdDatePicker } from 'antd';
import React, { ComponentProps } from 'react';

import {
  dayjsToMoment,
  momentToDayjs,
  type DatetimeType,
  type Moment,
} from '@common/services/datetime';

const AntdRangePicker = AntdDatePicker.RangePicker;
type AntdRangeProps = ComponentProps<typeof AntdRangePicker>;

// Mirrors antd's `value` shape, which allows individual endpoints to be null
// (e.g., when the user has picked only the start of a range).
export type DateRange = [DatetimeType | null, DatetimeType | null] | null;
type MomentRange = [Moment | null, Moment | null] | null;

// antd's `defaultValue` / `defaultPickerValue` reject null endpoints — both
// ends must be set up-front — so the wrapper exposes a strict tuple here.
export type StrictDateRange = [DatetimeType, DatetimeType];

export type RangePickerProps = Omit<
  AntdRangeProps,
  'value' | 'defaultValue' | 'defaultPickerValue' | 'onChange' | 'disabledDate'
> & {
  value?: DateRange;
  defaultValue?: StrictDateRange;
  defaultPickerValue?: StrictDateRange;
  onChange?: (dates: DateRange, dateStrings: [string, string]) => void;
  disabledDate?: (current: DatetimeType) => boolean;
};

function rangeToMoment(range: DateRange | undefined): MomentRange | undefined {
  if (range === undefined) return undefined;
  if (range === null) return null;
  return [dayjsToMoment(range[0]), dayjsToMoment(range[1])];
}

function strictRangeToMoment(
  range: StrictDateRange | undefined
): [Moment, Moment] | undefined {
  if (range === undefined) return undefined;
  return [dayjsToMoment(range[0]), dayjsToMoment(range[1])];
}

function rangeFromMoment(range: MomentRange): DateRange {
  if (range === null) return null;
  return [momentToDayjs(range[0]), momentToDayjs(range[1])];
}

export function RangePicker({
  value,
  defaultValue,
  defaultPickerValue,
  onChange,
  disabledDate,
  ...rest
}: RangePickerProps) {
  return (
    <AntdRangePicker
      {...rest}
      value={rangeToMoment(value)}
      defaultValue={strictRangeToMoment(defaultValue)}
      defaultPickerValue={strictRangeToMoment(defaultPickerValue)}
      onChange={
        onChange
          ? (dates, dateStrings) =>
              onChange(rangeFromMoment(dates), dateStrings)
          : undefined
      }
      disabledDate={
        disabledDate
          ? (current) => disabledDate(momentToDayjs(current))
          : undefined
      }
    />
  );
}
